import { action, computed, makeObservable, observable } from 'mobx';

import { analytics } from '../../services/analytics';
import type { ActionResult, ClickArea, EventAction, EventTriggerBy } from '../../services/analytics/types';
import { getRequest } from '../../services/api';
import { logToNewRelic } from '../../services/debug';
import { resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { devError } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import { formatTime } from '../../utils/time';
import { type CoinType } from '../cointype/CoinTypeStore';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { GameLabel } from '../game/types';
import { isFreeSpinsBundleItem } from '../rewards/helpers';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';
import { EnumOpenDialog } from '../ui/types';

import type { DailyMissionsResponse, GrandPrizeReward, UserDailyMission } from './types';

export class DailyMissionsStore extends Loadable implements IFeatureManagerStore {
  private root: RootStore;
  constructor(root: RootStore) {
    super();
    this.root = root;
    makeObservable(this);
  }

  @observable
  public enableGiftAnimation = false;

  @observable
  public grandPrizeAnimationShowed: CoinType[] = [];
  @action setGrandPrizeAnimationShowed(value: CoinType) {
    this.grandPrizeAnimationShowed = [...this.grandPrizeAnimationShowed, value];
  }

  @computed
  get isGrandPrizeAnimationShowed() {
    return this.grandPrizeAnimationShowed.includes(this.root.coinTypeStore.coinType);
  }

  @observable
  public dailyMissionGameLabel: GameLabel | null = null;

  @observable
  public currentGrandPrizeGift: GrandPrizeReward | null = null;
  @action
  public setCurrentGrandPrizeGift(value: GrandPrizeReward | null) {
    this.currentGrandPrizeGift = value;
  }

  public openGrandGiftDialog() {
    this.root.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenDailyMissionGrandPrizeGiftDialog });
  }

  public closeGrandGiftDialog(args?: { bypassFeatureManager?: boolean; preventNextDialog?: boolean }) {
    this.setCurrentGrandPrizeGift(null);
    this.root.uiStore.setCloseDialog();
    const nextGrandPrize = this.getNextGrandPrizeGift();
    this.setCurrentGrandPrizeGift(nextGrandPrize);
    if (nextGrandPrize && !args?.preventNextDialog) {
      setTimeout(() => {
        this.popGrandPrizeGift(nextGrandPrize);
        this.openGrandGiftDialog();
      }, 500);
    } else if (!args?.bypassFeatureManager) {
      this.root.featureManagerStore.onDialogFinished('DailyMissionGrandPrize');
    }
  }

  @action
  public setEnableGiftAnimation(value: boolean) {
    this.enableGiftAnimation = value;
  }

  dialogTypes: FeatureManagerDialogType[] = ['DailyMission', 'DailyMissionReward', 'DailyMissionGrandPrize'];

  public startDialogs(type?: FeatureManagerDialogType | undefined) {
    switch (type) {
      case 'DailyMission': {
        const coinType = this.root.coinTypeStore.coinType;

        // don't auto open for GC daily missions
        if (coinType === 'GC') {
          return false;
        }

        if (!this.activeScMissionShowed && this.currentDailyMission?.status === 'ACTIVE') {
          this.setDialogVisible(true);
          this.setActiveScMissionShowed();
          this.sendAnalyticsEvent({
            event_action: 'impression',
            triggered_by: 'login',
          });
          return true;
        }

        return false;
      }
      case 'DailyMissionReward': {
        if (this.currentDailyMission?.status === 'COMPLETED') {
          this.setEnableGiftAnimation(true);
          this.sendAnalyticsEvent({
            event_action: 'impression',
            triggered_by: 'login',
          });
          return true;
        }
        return false;
      }
      case 'DailyMissionGrandPrize': {
        const featureFlag = resolveFlag('daily_mission_grand_prize_enable', true);
        if (!featureFlag) {
          return false;
        }

        const grandPrize = this.getNextGrandPrizeGift();

        if (grandPrize) {
          this.setCurrentGrandPrizeGift(grandPrize);
          this.popGrandPrizeGift(grandPrize);
          this.openGrandGiftDialog();
          return true;
        }

        return false;
      }
      default: {
        return false;
      }
    }
  }

  private popGrandPrizeGift(grandPrize: GrandPrizeReward) {
    if (grandPrize.coinType === 'GC') {
      this.setGrandPrizesRewards({
        gcGrandPrizesRewards: this.gcGrandPrizesRewards.filter((item) => item.id !== grandPrize.id),
      });
    } else {
      this.setGrandPrizesRewards({
        scGrandPrizesRewards: this.scGrandPrizesRewards.filter((item) => item.id !== grandPrize.id),
      });
    }
  }

  private getNextGrandPrizeGift() {
    const grandGifts = [...this.scGrandPrizesRewards, ...this.gcGrandPrizesRewards];
    if (grandGifts.length > 0) {
      const grandPrize = grandGifts[0];
      return grandPrize;
    }
    return null;
  }

  public sendAnalyticsEvent(args: {
    event_action: EventAction;
    triggered_by?: EventTriggerBy;
    action_result?: ActionResult;
    button_text?: string;
    click_location?: ClickArea;
  }) {
    try {
      if (!this.currentDailyMission) return;
      const { event_action, triggered_by, button_text, click_location, action_result } = args;
      const { DailyMission, DailyMissionReward } = this.root.featureManagerStore.dialogManagerData || {};
      const isRewardDialog = this.currentDailyMission.status === 'COMPLETED';

      const feature_order = (isRewardDialog ? DailyMissionReward?.order : DailyMission?.order) || 0;
      const widget_sub_type = isRewardDialog ? 'daily_mission_reward' : 'daily_mission';
      let free_spins_amount = 0;
      if (this.currentDailyMission?.packageData?.bundleItems?.[0]) {
        const bundleItem = this.currentDailyMission.packageData.bundleItems[0];
        free_spins_amount = isFreeSpinsBundleItem(bundleItem) ? bundleItem.bundlePayload.rounds : 0;
      }

      const grandPrizeFeatureFlag = resolveFlag('daily_mission_grand_prize_enable', true);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      void analytics.sendEvent('widget', {
        event_action,
        feature_type: 'daily_mission',
        grand_prize_display: grandPrizeFeatureFlag,
        widget_sub_type,
        triggered_by,
        feature_order,
        button_text,
        click_location,
        action_result,
        mission_id: this.currentDailyMission.id,
        mission_type: this.currentDailyMission.missionType,
        mission_status: this.currentDailyMission.status,
        target_amount: this.currentDailyMission.goal,
        coin_type: this.currentDailyMission.coinType === 'GC' ? 'CC' : this.currentDailyMission.coinType,
        sc_amount: this.currentDailyMission?.packageData?.scCoin,
        cc_amount: this.currentDailyMission?.packageData?.gcCoin,
        free_spins_amount,
      });
    } catch (e) {
      devError('DailyMissionsStore.sendAnalyticsEvent', e);
    }
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    const featureFlag = resolveFlag('daily_missions_feature', true);
    const dailyMissionsV2FeatureFlag = resolveFlag('missions_v2_frontend_enabled', false);

    if (!featureFlag || dailyMissionsV2FeatureFlag) return 'OFF';

    try {
      this.setFetchingDailyMissions(true);
      const response = await getRequest<ApiResponse<DailyMissionsResponse>>('/v2/daily-missions');
      if (response?.data?.data) {
        this.setApiData(response.data.data);
        return 'READY';
      }
      return 'OFF';
    } catch (error) {
      devError('DailyMissionsStore.fetch', error);
      return 'ERROR';
    } finally {
      this.setFetchingDailyMissions(false);
    }
  });

  private setApiData(data: DailyMissionsResponse) {
    const {
      gcMissions,
      scMissions,
      scRewards,
      gcRewards,
      scGrandPrize,
      gcGrandPrize,
      gcGrandPrizesRewards,
      scGrandPrizesRewards,
      gameLabel,
    } = data;

    this.setDailyMissions(scMissions, gcMissions);
    this.setDailyMissionsRewards(scRewards, gcRewards);
    this.setGrandPrizes(gcGrandPrize, scGrandPrize);
    this.setGrandPrizesRewards({ gcGrandPrizesRewards, scGrandPrizesRewards });
    this.setNextMission('SC');
    this.setNextMission('GC');
    this.setDailyMissionGameLabel(gameLabel);
    this.runTimers();
  }

  public activeScMissionShowed = false;
  public activeGcMissionShowed = false;
  setActiveScMissionShowed() {
    this.activeScMissionShowed = true;
  }

  @computed
  public get currentDailyMission() {
    return this.root.coinTypeStore.coinType === 'GC' ? this.currentGcMission : this.currentScMission;
  }

  @computed
  public get currentGrandPrize() {
    return this.root.coinTypeStore.coinType === 'GC' ? this.gcGrandPrize : this.scGrandPrize;
  }

  // Using to prevent other dialogs to be shown when daily mission dialog is visible or should be
  @computed
  public get isDailyMissionBusy() {
    if (this.isDialogVisible || this.fetchingDailyMissions) {
      return true;
    }

    // Active mission is not showed yet after login
    if (this.root.coinTypeStore.coinType === 'GC') {
      if (this.currentGcMission?.status === 'ACTIVE' && !this.activeGcMissionShowed) {
        return true;
      }
      if (this.currentGcMission?.status === 'COMPLETED') {
        return true;
      }
    }
    // Active mission is not showed yet after login
    if (this.root.coinTypeStore.coinType === 'SC') {
      if (this.currentScMission?.status === 'ACTIVE' && !this.activeScMissionShowed) {
        return true;
      }
      if (this.currentScMission?.status === 'COMPLETED') {
        return true;
      }
    }
    return false;
  }

  @observable
  public scTimer = 0;
  @observable
  public gcTimer = 0;
  @action
  public setTimer(value: number, coinType: CoinType) {
    if (coinType === 'GC') {
      this.gcTimer = value;
    } else if (coinType === 'SC') {
      this.scTimer = value;
    }
  }

  public getTimer(coinType: CoinType) {
    const timer = coinType === 'GC' ? this.gcTimer : this.scTimer;
    return timer ? formatTime(timer, 's') : null;
  }

  public getRawTimer(coinType: CoinType) {
    return coinType === 'GC' ? this.gcTimer : this.scTimer;
  }

  private getActualMissionForTimer() {
    let activeScMission = this.scUserDailyMissions.find((mission) => mission.status === 'ACTIVE');
    if (!activeScMission) {
      activeScMission = this.scUserDailyMissions.find((mission) => mission.status === 'UPCOMING');
    }

    let activeGcMission = this.gcUserDailyMissions.find((mission) => mission.status === 'ACTIVE');
    if (!activeGcMission) {
      activeGcMission = this.gcUserDailyMissions.find((mission) => mission.status === 'UPCOMING');
    }
    return { activeGcMission, activeScMission };
  }

  private interval: NodeJS.Timeout | number = 0;
  private runTimers() {
    const { activeScMission, activeGcMission } = this.getActualMissionForTimer();
    if (activeScMission) {
      this.setTimer(activeScMission.timeLeft, 'SC');
    }
    if (activeGcMission) {
      this.setTimer(activeGcMission.timeLeft, 'GC');
    }

    if (!this.scTimer && !this.gcTimer) return;

    clearInterval(this.interval);
    this.interval = setInterval(() => {
      if (this.scTimer > 0) {
        this.setTimer(this.scTimer - 1, 'SC');
      } else if (activeScMission) {
        void this.fetch()
          .then(() => {
            this.root.featureManagerStore.addDialogsToSequenceQueue(['DailyMission']);
          })
          .catch((error) => {
            devError('DailyMissionsStore.runTimers', error);
          });
        clearInterval(this.interval);
        return;
      }
      if (this.gcTimer > 0) {
        this.setTimer(this.gcTimer - 1, 'GC');
      } else if (activeGcMission) {
        void this.fetch()
          .then(() => {
            this.root.featureManagerStore.addDialogsToSequenceQueue(['DailyMission']);
          })
          .catch((error) => {
            devError('DailyMissionsStore.runTimers', error);
          });
        clearInterval(this.interval);
        return;
      }
    }, 1000);
  }

  @observable
  public isLoading = false;
  @action
  public setIsLoading(value: boolean) {
    this.isLoading = value;
  }

  @observable
  public isDialogVisible = false;
  @action
  public setDialogVisible(value: boolean) {
    if (value) {
      this.root.uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenDailyMissionDialog,
      });
    } else {
      this.root.uiStore.setCloseDialog();
    }
    this.isDialogVisible = value;
  }

  @observable
  public currentScMission: UserDailyMission | null = null;
  @observable
  public currentGcMission: UserDailyMission | null = null;
  @action
  public setCurrentMission(mission: UserDailyMission | null, coinType: CoinType) {
    if (coinType === 'GC') {
      this.currentGcMission = mission;
    } else if (coinType === 'SC') {
      this.currentScMission = mission;
    }
  }

  public findNextMission(coinType: CoinType) {
    let mission = null;
    const rewards = coinType === 'GC' ? this.gcRewards : this.scRewards;
    const missionList = coinType === 'GC' ? this.gcUserDailyMissions : this.scUserDailyMissions;

    if (rewards.length > 0) {
      mission = rewards.reduce((prev, current) => (prev.order < current.order ? prev : current));
    }
    if (!mission) {
      mission = missionList.find((missionItem) => missionItem.status === 'ACTIVE');
      if (!mission) {
        mission = missionList.find((missionItem) => missionItem.status === 'UPCOMING');
      }
    }
    return mission || null;
  }

  @action
  public setNextMission(coinType: CoinType) {
    const nextMission = this.findNextMission(coinType);
    this.setCurrentMission(nextMission, coinType);
  }

  @action
  public setDailyMissionGameLabel(gameLabel: GameLabel | null) {
    this.dailyMissionGameLabel = gameLabel;
  }

  @action
  public removeMissionById(id: string) {
    // this.scUserDailyMissions = this.scUserDailyMissions.filter((mission) => mission.id !== id);
    // this.gcUserDailyMissions = this.gcUserDailyMissions.filter((mission) => mission.id !== id);
    this.scRewards = this.scRewards.filter((mission) => mission.id !== id);
    this.gcRewards = this.gcRewards.filter((mission) => mission.id !== id);
    if (this.currentGcMission?.id === id) {
      this.currentGcMission = null;
    }
    if (this.currentScMission?.id === id) {
      this.currentScMission = null;
    }
  }

  @observable
  public scUserDailyMissions: UserDailyMission[] = [];
  @observable
  public gcUserDailyMissions: UserDailyMission[] = [];
  @action
  public setDailyMissions(scDailyMissions?: UserDailyMission[], gcDailyMissions?: UserDailyMission[]) {
    if (Array.isArray(scDailyMissions)) {
      this.scUserDailyMissions = scDailyMissions.sort((a, b) => b.order - a.order);
    }
    if (Array.isArray(gcDailyMissions)) {
      this.gcUserDailyMissions = gcDailyMissions.sort((a, b) => b.order - a.order);
    }
  }

  @observable
  public scGrandPrize: GrandPrizeReward | null = null;
  @observable
  public gcGrandPrize: GrandPrizeReward | null = null;
  @action
  public setGrandPrizes(gcGrandPrize: GrandPrizeReward | null, scGrandPrize: GrandPrizeReward | null) {
    this.scGrandPrize = scGrandPrize;
    this.gcGrandPrize = gcGrandPrize;
  }

  @observable
  public scGrandPrizesRewards: GrandPrizeReward[] = [];
  @observable
  public gcGrandPrizesRewards: GrandPrizeReward[] = [];
  @action
  public setGrandPrizesRewards(args: {
    scGrandPrizesRewards?: GrandPrizeReward[];
    gcGrandPrizesRewards?: GrandPrizeReward[];
  }) {
    const { scGrandPrizesRewards, gcGrandPrizesRewards } = args;
    if (Array.isArray(scGrandPrizesRewards)) {
      this.scGrandPrizesRewards = scGrandPrizesRewards;
    }
    if (Array.isArray(gcGrandPrizesRewards)) {
      this.gcGrandPrizesRewards = gcGrandPrizesRewards;
    }
  }

  @observable
  public scRewards: UserDailyMission[] = [];
  @observable
  public gcRewards: UserDailyMission[] = [];
  public setDailyMissionsRewards(scRewards?: UserDailyMission[], gcRewards?: UserDailyMission[]) {
    if (Array.isArray(scRewards)) {
      this.scRewards = scRewards;
    }
    if (Array.isArray(gcRewards)) {
      this.gcRewards = gcRewards;
    }
  }

  public fetchingDailyMissions = false;
  public setFetchingDailyMissions(value: boolean) {
    this.fetchingDailyMissions = value;
  }

  @action
  public reset() {
    clearInterval(this.interval);
    this.interval = 0;
    this.scTimer = 0;
    this.gcTimer = 0;
    this.currentScMission = null;
    this.currentGcMission = null;
    this.scUserDailyMissions = [];
    this.gcUserDailyMissions = [];
    this.scRewards = [];
    this.gcRewards = [];
    this.scGrandPrize = null;
    this.gcGrandPrize = null;
    this.scGrandPrizesRewards = [];
    this.gcGrandPrizesRewards = [];
    this.dailyMissionGameLabel = null;
    this.currentGrandPrizeGift = null;
    this.enableGiftAnimation = false;
    this.grandPrizeAnimationShowed = [];
    this.isLoading = false;
    this.isDialogVisible = false;
    this.activeScMissionShowed = false;
    this.activeGcMissionShowed = false;
    this.fetchingDailyMissions = false;
    this.setLoading(false);
  }

  public async fetchDailyMissions() {
    try {
      if (this.fetchingDailyMissions) return;
      this.setFetchingDailyMissions(true);
      const response = await getRequest<ApiResponse<DailyMissionsResponse>>('daily-missions');
      if (response?.data?.data) {
        const { gcMissions, scMissions, scRewards, gcRewards } = response.data.data;

        this.setDailyMissions(scMissions, gcMissions);
        this.setDailyMissionsRewards(scRewards, gcRewards);
        this.setNextMission('SC');
        this.setNextMission('GC');
        this.runTimers();
      }
    } catch (error) {
      logToNewRelic('FetchDailyMissionsFrontError', JSON.stringify({ error }), 'error');
    }
    this.setFetchingDailyMissions(false);
  }
}
